import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { MaskedLines, FadeIn, Overline } from "@/components/motion/Reveal";
import { Seo } from "@/components/Seo";
import { SERVICES, MISSION_CTA, PACKAGES, HEALTH_CHECK, RETAINERS } from "@/constants/content";

const ServicesHero = () => (
  <section data-testid="services-hero" className="pt-48 pb-24">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <FadeIn y={16}>
        <Overline>Services</Overline>
      </FadeIn>
      <MaskedLines
        className="mt-8 font-serif font-light tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-8xl"
        delay={0.15}
        lines={["Three pillars of", <span key="l2" className="italic text-primary">control.</span>]}
      />
      <FadeIn delay={0.5} className="mt-12 max-w-2xl">
        <p className="text-base lg:text-lg font-light text-muted-foreground leading-relaxed">
          Every engagement draws on one or more of our four practices — integrated,
          so the structure we build holds together as one system.
        </p>
      </FadeIn>
    </div>
  </section>
);

const Pillar = ({ service, index }) => {
  const reversed = index % 2 === 1;
  return (
    <section id={service.slug} data-testid={`pillar-${service.slug}`} className="py-24 lg:py-32 border-t border-white/5 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <FadeIn className={`lg:col-span-5 ${reversed ? "lg:order-2 lg:col-start-8" : ""}`}>
          <div className="relative overflow-hidden group">
            <img
              src={service.image}
              alt={service.title}
              className="w-full aspect-[4/5] object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700 ease-out"
            />
            <div className="absolute inset-0 bg-slate-900/30 group-hover:bg-slate-900/10 transition-colors duration-700" />
            <span className="absolute bottom-6 left-6 font-serif text-7xl font-light text-white/40">{service.number}</span>
          </div>
        </FadeIn>

        <div className={`lg:col-span-6 ${reversed ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
          <FadeIn>
            <Overline>Pillar {service.number}</Overline>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-light tracking-tight leading-tight">{service.title}</h2>
            <p className="mt-4 font-serif italic text-xl lg:text-2xl font-light text-primary/90">{service.tagline}</p>
            <p data-testid={`pillar-team-${service.slug}`} className="mt-3 text-xs tracking-[0.15em] uppercase text-muted-foreground">{service.team}</p>
            <p className="mt-8 text-base font-light text-muted-foreground leading-relaxed">{service.description}</p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <ul className="mt-10 space-y-4">
              {service.items.map((item) => (
                <li key={item} className="flex items-center gap-4 text-sm text-foreground/90">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid={`pillar-contact-${service.slug}`}
              className="mt-12 inline-flex items-center gap-2 border border-white/20 text-sm px-8 py-4 hover:border-primary hover:text-primary transition-colors duration-300"
            >
              Discuss this pillar <ArrowUpRight className="w-4 h-4" />
            </Link>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

const PackagesSection = () => (
  <section data-testid="packages-section" className="py-32 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <FadeIn className="mb-16">
        <Overline>Solutions by Stage</Overline>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-light tracking-tight">Solutions for every stage of growth.</h2>
        <p className="mt-6 max-w-2xl text-base font-light text-muted-foreground leading-relaxed">
          Every engagement is scoped to your needs — we tailor the mix rather than sell a fixed package.
        </p>
      </FadeIn>

      <FadeIn className="mb-px">
        <div data-testid="health-check-card" className="border border-primary/40 bg-primary/[0.04] p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <Overline>Start here</Overline>
            <h3 className="mt-4 font-serif text-3xl lg:text-4xl font-light">{HEALTH_CHECK.name}</h3>
            <p className="mt-4 text-sm lg:text-base font-light text-muted-foreground leading-relaxed max-w-xl">{HEALTH_CHECK.description}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {HEALTH_CHECK.includes.map((i) => (
                <span key={i} className="text-xs border border-white/15 px-4 py-2 text-foreground/80">{i}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">One-time review</p>
            <Link
              to="/contact"
              data-testid="health-check-cta"
              className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-8 py-4 hover:scale-105 transition-transform duration-300"
            >
              Ask about the Health Check <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </FadeIn>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
        {PACKAGES.map((p, i) => (
          <FadeIn key={p.number} delay={i * 0.06} className="bg-background">
            <div data-testid={`package-${p.number}`} className="p-10 lg:p-12 h-full flex flex-col group border border-transparent hover:border-white/10 transition-colors duration-500">
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-serif text-4xl font-light text-white/15 group-hover:text-primary/60 transition-colors duration-500">{p.number}</span>
                <span className="text-xs tracking-[0.15em] uppercase text-muted-foreground text-right">{p.target}</span>
              </div>
              <h3 className="mt-6 font-serif text-2xl lg:text-3xl font-light">{p.name}</h3>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm font-light text-foreground/85">
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid={`package-contact-${p.number}`}
                className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-primary transition-colors duration-300"
              >
                Discuss this package <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const RetainersSection = () => (
  <section data-testid="retainers-section" className="py-32 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <FadeIn className="mb-16">
        <Overline>Ongoing Support</Overline>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-light tracking-tight">Monthly retainers.</h2>
        <p className="mt-6 max-w-2xl text-base font-light text-muted-foreground leading-relaxed">
          Your on-call team of specialists — continuous support without the full-time hire.
        </p>
      </FadeIn>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
        {RETAINERS.map((r, i) => (
          <FadeIn key={r.slug} delay={i * 0.06} className="bg-background">
            <div
              data-testid={`retainer-${r.slug}`}
              className={`p-10 lg:p-12 h-full flex flex-col border transition-colors duration-500 ${
                r.featured ? "border-primary/40 bg-primary/[0.04]" : "border-transparent hover:border-white/10"
              }`}
            >
              <h3 className="font-serif text-2xl font-light">{r.name}</h3>
              <p className="mt-4 text-sm font-light text-muted-foreground leading-relaxed flex-1">{r.description}</p>
              <Link
                to="/contact"
                data-testid={`retainer-contact-${r.slug}`}
                className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-primary transition-colors duration-300"
              >
                Ask about this retainer <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const MissionBand = () => (
  <section data-testid="services-mission-band" className="py-32 lg:py-40 border-t border-white/5 bg-[#0B1322]">
    <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
      <FadeIn>
        <Overline>Our promise</Overline>
        <p data-testid="services-mission-statement" className="mt-10 font-serif text-3xl sm:text-4xl lg:text-6xl font-light leading-tight">
          {MISSION_CTA}
        </p>
        <Link
          to="/contact"
          data-testid="mission-band-contact-button"
          className="mt-14 inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-10 py-5 hover:scale-105 transition-transform duration-300"
        >
          Get in Touch <ArrowUpRight className="w-4 h-4" />
        </Link>
      </FadeIn>
    </div>
  </section>
);

export default function Services() {
  return (
    <main data-testid="services-page">
      <Seo
        title="Services — Clear Horizon Consulting"
        description="Organizational development & management systems, legal & compliance, and financial & accounting services — plus stage-based solutions and monthly retainers."
        path="/services"
      />
      <h1 className="sr-only">Consulting Services — Organizational Development, Legal & Compliance, Financial & Accounting</h1>
      <ServicesHero />
      {SERVICES.map((s, i) => (
        <Pillar key={s.slug} service={s} index={i} />
      ))}
      <PackagesSection />
      <RetainersSection />
      <MissionBand />
    </main>
  );
}
